import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ConsultationBooking = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [meetingType, setMeetingType] = useState('');
  const [isBooking, setIsBooking] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    let currentDate = new Date(today);
    
    for (let i = 0; i < 30; i++) {
      currentDate.setDate(today.getDate() + i);
      const dayOfWeek = currentDate.getDay();
      
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        const dateString = currentDate.toISOString().split('T')[0];
        const displayDate = currentDate.toLocaleDateString('es-ES', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        dates.push({
          value: dateString,
          label: displayDate.charAt(0).toUpperCase() + displayDate.slice(1)
        });
      }
    }
    
    return dates;
  };

  const availableTimes = [
    { value: '09:00', label: '9:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '15:00', label: '3:00 PM' },
    { value: '16:00', label: '4:00 PM' },
    { value: '17:00', label: '5:00 PM' }
  ];

  const consultants = [
    {
      value: 'maria-rodriguez',
      label: 'María Rodríguez - Consultora Senior ERP',
      description: 'Especialista en implementación y migración'
    },
    {
      value: 'carlos-mendez',
      label: 'Carlos Méndez - Arquitecto de Soluciones',
      description: 'Experto en sistemas empresariales complejos'
    },
    {
      value: 'ana-garcia',
      label: 'Ana García - Consultora de Procesos',
      description: 'Especialista en optimización de procesos'
    },
    {
      value: 'any-consultant',
      label: 'Cualquier consultor disponible',
      description: 'El primer consultor disponible en el horario'
    }
  ];

  const meetingTypes = [
    {
      value: 'video',
      label: 'Videollamada',
      description: 'Reunión virtual por Zoom/Teams',
      icon: 'Video'
    },
    {
      value: 'phone',
      label: 'Llamada telefónica',
      description: 'Consulta por teléfono',
      icon: 'Phone'
    },
    {
      value: 'in-person',
      label: 'Reunión presencial',
      description: 'En nuestras oficinas',
      icon: 'Users'
    }
  ];

  const handleBookConsultation = async () => {
    if (!selectedDate || !selectedTime || !meetingType) {
      return;
    }

    setIsBooking(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error booking consultation:', error);
    } finally {
      setIsBooking(false);
    }
  };

  const resetBooking = () => {
    setSelectedDate('');
    setSelectedTime('');
    setSelectedConsultant('');
    setMeetingType('');
    setShowConfirmation(false);
  };

  if (showConfirmation) {
    const selectedDateObj = new Date(selectedDate);
    const formattedDate = selectedDateObj.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    const selectedTimeLabel = availableTimes.find(t => t.value === selectedTime)?.label;
    const selectedConsultantData = consultants.find(c => c.value === selectedConsultant);
    const selectedMeetingData = meetingTypes.find(m => m.value === meetingType);

    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-6">
            <Icon name="Calendar" size={32} className="text-black" />
          </div>

          <h3 className="text-2xl font-semibold text-white mb-4">
            ¡Consulta Agendada!
          </h3>

          <div className="bg-white/5 rounded-lg p-6 mb-6 text-left border border-white/10">
            <h4 className="font-semibold text-white mb-4">Detalles de tu consulta:</h4>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Icon name="Calendar" size={16} className="text-[#A78BFA]" />
                <span className="text-white/80 font-medium">
                  {formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)}
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <Icon name="Clock" size={16} className="text-[#A78BFA]" />
                <span className="text-white/80 font-medium">{selectedTimeLabel}</span>
              </div>

              {selectedConsultantData && selectedConsultantData.value !== 'any-consultant' && (
                <div className="flex items-center space-x-3">
                  <Icon name="User" size={16} className="text-[#A78BFA]" />
                  <span className="text-white/80 font-medium">{selectedConsultantData.label}</span>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <Icon name={selectedMeetingData.icon} size={16} className="text-[#A78BFA]" />
                <span className="text-white/80 font-medium">{selectedMeetingData.label}</span>
              </div>
            </div>
          </div>

          <p className="text-white/60 mb-6">
            Recibirás un correo de confirmación con los detalles de la reunión y el enlace de acceso (si aplica).
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="outline"
              onClick={resetBooking}
              iconName="Plus"
              iconPosition="left"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Agendar Otra Consulta
            </Button>

            <Button
              variant="default"
              onClick={() => window.location.href = '/'}
              iconName="Home"
              iconPosition="left"
              className="bg-white hover:opacity-80 text-black font-medium"
            >
              Volver al Inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    // El <Select> compartido usa text-foreground para su label, que bajo
    // force-light-mode es casi negro -- sobre este fondo negro se overridea
    // localmente para que el label se lea en blanco.
    <div
      className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8"
      style={{ '--color-foreground': '#ffffff' }}
    >
      <div className="mb-8">
        <h2 className="text-xl sm:text-2xl font-semibold text-white mb-3">
          Agenda tu Consulta
        </h2>
        <p className="text-white/60">
          Selecciona la fecha, hora y tipo de reunión que mejor se adapte a tu horario.
        </p>
      </div>

      <div className="space-y-6">
        <Select
          label="Fecha de la Consulta"
          options={generateAvailableDates()}
          value={selectedDate}
          onChange={setSelectedDate}
          placeholder="Selecciona una fecha"
          searchable
          required
        />

        {selectedDate && (
          <Select
            label="Hora de la Consulta"
            options={availableTimes}
            value={selectedTime}
            onChange={setSelectedTime}
            placeholder="Selecciona una hora"
            required
          />
        )}

        {selectedTime && (
          <div>
            <label className="block text-sm font-medium text-white/70 mb-4">
              Tipo de Reunión *
            </label>
            <div className="grid grid-cols-1 gap-4">
              {meetingTypes.map((type) => (
                <div
                  key={type.value}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${
                    meetingType === type.value
                      ? 'border-[#7C3AED] bg-[#7C3AED]/10' : 'border-white/10 hover:border-white/25 hover:bg-white/5'
                  }`}
                  onClick={() => setMeetingType(type.value)}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      meetingType === type.value
                        ? 'bg-[#7C3AED] text-white' : 'bg-white/10 text-white/40'
                    }`}>
                      <Icon name={type.icon} size={20} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white">{type.label}</h4>
                      <p className="text-sm text-white/50">{type.description}</p>
                    </div>
                    {meetingType === type.value && (
                      <Icon name="Check" size={20} className="text-[#A78BFA]" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== CAMPO DE CONSULTOR COMENTADO ===== */}
        {/*
        {meetingType && (
          <Select
            label="Consultor Preferido (Opcional)"
            options={consultants}
            value={selectedConsultant}
            onChange={setSelectedConsultant}
            placeholder="Selecciona un consultor o deja en blanco"
            description="Si no seleccionas un consultor específico, asignaremos el mejor disponible para tu caso"
          />
        )}
        */}
        {/* ======================================= */}

        {selectedDate && selectedTime && meetingType && (
          <div className="bg-white/5 rounded-lg p-6 border border-white/10 mt-6">
            <h4 className="font-semibold text-white mb-4 flex items-center">
              <Icon name="Calendar" size={20} className="mr-2 text-[#A78BFA]" />
              Resumen de tu Consulta
            </h4>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Fecha:</span>
                <span className="text-white font-medium">
                  {new Date(selectedDate).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-white/50">Hora:</span>
                <span className="text-white font-medium">
                  {availableTimes.find(t => t.value === selectedTime)?.label}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-white/50">Tipo:</span>
                <span className="text-white font-medium">
                  {meetingTypes.find(m => m.value === meetingType)?.label}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-white/50">Duración:</span>
                <span className="text-white font-medium">60 minutos</span>
              </div>
            </div>
          </div>
        )}

        <Button
          variant="default"
          size="lg"
          fullWidth
          onClick={handleBookConsultation}
          loading={isBooking}
          disabled={!selectedDate || !selectedTime || !meetingType}
          className="bg-white hover:opacity-80 text-black font-medium mt-4"
          iconName="Calendar"
          iconPosition="left"
        >
          {isBooking ? 'Agendando...' : 'Confirmar Consulta'}
        </Button>

        <div className="text-center pt-4">
          <p className="text-sm text-white/50">
            La consulta inicial es completamente gratuita y sin compromiso.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsultationBooking;